import React, { useState, useEffect, useCallback, useRef } from "react";
import { AuthProvider, useAuth } from "./AuthProvider";
import { TaskPaneHeader } from "./TaskPaneHeader";
import { SignInScreen } from "./SignInScreen";
import { AuthLoadingScreen } from "./AuthLoadingScreen";
import { SessionExpiredBanner } from "./SessionExpiredBanner";
import { OfflineNotice } from "./OfflineNotice";
import { Alert } from "./Alert";
import { EmptyState } from "./EmptyState";
import { ImportCard } from "./ImportCard";
import { CommandPalette } from "./CommandPalette";
import { ChevronDown } from "lucide-react";
import type { DataSource, ImportCardData } from "../types/imports";
import { executeSourceQueryStream, ApiRequestError } from "../services/api";
import type { DateFilterParam } from "../services/api";
import {
  importFromCsvStream,
  activateSheetById,
  getSheetNameById,
  refreshTableFromCsvStream,
  computeTableContentHash,
  getTableColumnNames,
} from "../services/excel";
import {
  saveImportMetadata,
  readAllImportMetadata,
  deleteImportMetadata,
  updateImportMetadata,
  setRefreshLock,
  clearRefreshLock,
  checkRefreshLock,
} from "../services/xmlParts";

function TaskPaneContent() {
  const { user, authState, login, logout, markExpired } = useAuth();

  // Org selection — single org, no selector needed
  const orgs = user?.getOrgs() ?? [];
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  // Import cards
  const [importCards, setImportCards] = useState<ImportCardData[]>([]);

  // Command palette state
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Refresh All state
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);

  // XML restore warning (shown briefly if some XML Parts couldn't be parsed)
  const [xmlRestoreWarning, setXmlRestoreWarning] = useState<string>();


  // Scroll container ref + "show scroll down" state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const checkScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight;
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      setShowScrollDown(hasOverflow && !isNearBottom);
    };

    checkScroll();
    el.addEventListener("scroll", checkScroll);
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer.disconnect();
    };
  }, [importCards.length]);

  const handleScrollToBottom = () => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  };

  // Offline detection
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  // Set org ID to the user's single org on mount
  const userId = user?.userId ?? null;
  useEffect(() => {
    if (orgs.length > 0) {
      setSelectedOrgId(orgs[0].orgId);
    } else {
      setSelectedOrgId(null);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore imported cards from XML Parts once authenticated, then resolve live sheet names
  useEffect(() => {
    if (authState !== "authenticated") return;
    readAllImportMetadata()
      .then(async ({ cards: restored, skippedCount }) => {
        if (skippedCount > 0) {
          setXmlRestoreWarning("Some import connections couldn't be restored.");
          setTimeout(() => setXmlRestoreWarning(undefined), 5000);
        }
        if (restored.length === 0) return;

        // Resolve live sheet names from Excel (handles renames while pane was closed)
        try {
          await Excel.run(async (context) => {
            const sheets = context.workbook.worksheets;
            sheets.load("items/id, items/name");
            await context.sync();

            const sheetMap = new Map(
              sheets.items.map((s) => [s.id, s.name]),
            );

            for (const card of restored) {
              if (card.sheetId) {
                const liveName = sheetMap.get(card.sheetId);
                if (liveName) {
                  card.sheetName = liveName;
                }
              }
            }
          });
        } catch {
          // Fall back to stored names if Excel context unavailable
        }

        setImportCards((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newCards = restored.filter((c) => !existingIds.has(c.id));
          return [...prev, ...newCards];
        });
      })
      .catch(() => {
        // XML Parts may not be available
      });
  }, [authState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to Excel worksheet name changes in real time (only when authenticated)
  useEffect(() => {
    if (authState !== "authenticated") return;

    let handler: OfficeExtension.EventHandlerResult<Excel.WorksheetNameChangedEventArgs> | null =
      null;
    let mounted = true;

    Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      handler = sheets.onNameChanged.add(async (event) => {
        if (!mounted) return;
        setImportCards((prev) =>
          prev.map((c) =>
            c.sheetId === event.worksheetId && c.status === "imported"
              ? { ...c, sheetName: event.nameAfter }
              : c,
          ),
        );
      });
      await context.sync();
    }).catch(() => {});

    return () => {
      mounted = false;
      if (handler) {
        Excel.run(async (context) => {
          handler!.remove();
          await context.sync();
        }).catch(() => {});
      }
    };
  }, [authState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick every 60s to keep relative timestamps fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  // Helper to update a single card's fields
  const updateCard = useCallback(
    (cardId: string, updates: Partial<ImportCardData>) => {
      setImportCards((prev) =>
        prev.map((c) => (c.id === cardId ? { ...c, ...updates } : c)),
      );
    },
    [],
  );

  if (authState === "loading") {
    return <AuthLoadingScreen />;
  }

  if (authState === "unauthenticated") {
    return <SignInScreen onSignIn={login} />;
  }

  // ---- Helpers ----

  function buildDateFilter(card: ImportCardData): DateFilterParam | undefined {
    if (!card.dateColumn || !card.datePreset) return undefined;
    const filter: DateFilterParam = {
      column: card.dateColumn,
      preset: card.datePreset,
    };
    if (card.datePreset === "after" && card.afterMonth) {
      filter.after_month = card.afterMonth;
    }
    return filter;
  }

  // ---- Handlers ----

  const handleSelectSource = (source: DataSource) => {
    const newCard: ImportCardData = {
      id: crypto.randomUUID(),
      dataSource: source,
      status: "pending",
    };
    setImportCards((prev) => [...prev, newCard]);
  };

  const handleDateFilterChange = (
    cardId: string,
    dateColumn: string | undefined,
    dateColumnDisplayName: string | undefined,
    datePreset: string | undefined,
    afterMonth: string | undefined,
  ) => {
    updateCard(cardId, {
      dateColumn,
      dateColumnDisplayName,
      datePreset,
      afterMonth,
    });
  };

  const handleImport = async (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card || !selectedOrgId || isOffline) return;

    updateCard(cardId, {
      status: "loading",
      loadingMessage: `Fetching ${card.dataSource.name}…`,
    });

    try {
      const dateFilter = buildDateFilter(card);

      // 1. Start streaming CSV from backend
      const stream = await executeSourceQueryStream(
        selectedOrgId,
        card.dataSource.id,
        card.dataSource.name,
        dateFilter,
      );

      // 2. Parse CSV stream and write to Excel in batches
      const writeResult = await importFromCsvStream(
        card.dataSource.name,
        stream,
        (progressMsg) => {
          updateCard(cardId, { loadingMessage: progressMsg });
        },
      );

      // 3. Compute content hash and column schema for change detection on refresh
      let contentHash: string | undefined;
      let columnSchema: string[] | undefined;
      try {
        contentHash = await computeTableContentHash(writeResult.tableName);
        columnSchema = await getTableColumnNames(writeResult.tableName);
      } catch {
        // Non-critical — refresh will just skip the check
      }

      // 4. Build updated card data
      const now = new Date().toISOString();
      const updatedCard: ImportCardData = {
        ...card,
        status: "imported",
        sheetId: writeResult.sheetId,
        sheetName: writeResult.sheetName,
        tableName: writeResult.tableName,
        lastRefreshed: now,
        rowCount: writeResult.rowCount,
        truncated: writeResult.truncated || false,
        contentHash,
        columnSchema,
        loadingMessage: undefined,
        errorMessage: undefined,
        warningMessage: undefined,
        refreshWarningType: undefined,
      };

      // 5. Persist to XML Parts
      const xmlPartId = await saveImportMetadata({
        ...updatedCard,
        lastRefreshed: now,
      });

      // 6. Update card state
      updateCard(cardId, { ...updatedCard, xmlPartId });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        markExpired();
        updateCard(cardId, { status: "pending", loadingMessage: undefined });
        return;
      }
      let message =
        error instanceof Error ? error.message : "Import failed";
      if (error instanceof ApiRequestError && error.status === 429) {
        message = "Too many requests \u2014 please wait a moment before refreshing.";
      }
      updateCard(cardId, {
        status: "error",
        errorMessage: message,
        loadingMessage: undefined,
      });
    } finally {
    }
  };

  const handleRefresh = async (cardId: string, skipChecks = false, skipLockCheck = false) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card || !selectedOrgId || !card.tableName || !card.sheetId || isOffline) return;

    // Co-authoring lock check (skip when called from Refresh All)
    if (!skipLockCheck) {
      try {
        const locked = await checkRefreshLock();
        if (locked) {
          updateCard(cardId, {
            status: "warning",
            warningMessage: "Another user is currently refreshing. Please wait.",
            refreshWarningType: "content-modified",
          });
          return;
        }
      } catch {
        // If we can't check, proceed anyway
      }
    }

    // Check if the sheet still exists
    try {
      const name = await getSheetNameById(card.sheetId);
      if (!name) {
        updateCard(cardId, {
          status: "error",
          errorMessage: "The destination sheet for this import has been deleted.",
          sheetDeleted: true,
          loadingMessage: undefined,
        });
        return;
      }
    } catch {
      // If we can't check, proceed anyway
    }

    // Content hash check (detect user edits)
    if (!skipChecks && card.contentHash) {
      try {
        const currentHash = await computeTableContentHash(card.tableName);
        if (currentHash !== card.contentHash) {
          updateCard(cardId, {
            status: "warning",
            warningMessage: `The data in '${card.sheetName}' has been modified since the last import. Refreshing will replace all data in the table with the latest from Cashboard. Any manual edits will be lost.`,
            refreshWarningType: "content-modified",
          });
          return;
        }
      } catch {
        // If we can't check, proceed anyway
      }
    }

    // Schema drift check
    if (!skipChecks && card.columnSchema) {
      try {
        const currentColumns = await getTableColumnNames(card.tableName);
        const schemaChanged =
          currentColumns.length !== card.columnSchema.length ||
          currentColumns.some((col: string, i: number) => col !== card.columnSchema![i]);
        if (schemaChanged) {
          updateCard(cardId, {
            status: "warning",
            warningMessage: `The columns in this data source have changed since the last import. Refreshing will update the table structure.`,
            refreshWarningType: "schema-drift",
          });
          return;
        }
      } catch {
        // If we can't check, proceed anyway
      }
    }

    updateCard(cardId, {
      status: "loading",
      loadingMessage: `Refreshing ${card.dataSource.name}…`,
      warningMessage: undefined,
      refreshWarningType: undefined,
    });

    await setRefreshLock();
    try {
      const dateFilter = buildDateFilter(card);

      const stream = await executeSourceQueryStream(
        selectedOrgId,
        card.dataSource.id,
        card.dataSource.name,
        dateFilter,
      );

      const writeResult = await refreshTableFromCsvStream(
        card.tableName,
        card.sheetId,
        stream,
        (progressMsg: string) => {
          updateCard(cardId, { loadingMessage: progressMsg });
        },
      );

      // Compute new content hash and column schema
      let contentHash: string | undefined;
      let columnSchema: string[] | undefined;
      try {
        contentHash = await computeTableContentHash(card.tableName);
        columnSchema = await getTableColumnNames(card.tableName);
      } catch {
        // Non-critical
      }

      const now = new Date().toISOString();
      const updatedCard: ImportCardData = {
        ...card,
        status: "imported",
        lastRefreshed: now,
        rowCount: writeResult.rowCount,
        truncated: writeResult.truncated || false,
        contentHash,
        columnSchema,
        loadingMessage: undefined,
        errorMessage: undefined,
        warningMessage: undefined,
        refreshWarningType: undefined,
      };

      const newXmlPartId = await updateImportMetadata({
        ...updatedCard,
        lastRefreshed: now,
      });

      updateCard(cardId, { ...updatedCard, xmlPartId: newXmlPartId });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        markExpired();
        updateCard(cardId, { status: "imported", loadingMessage: undefined });
        return;
      }
      let message =
        error instanceof Error ? error.message : "Refresh failed";
      if (error instanceof ApiRequestError && error.status === 429) {
        message = "Too many requests \u2014 please wait a moment before refreshing.";
      }
      updateCard(cardId, {
        status: "error",
        errorMessage: message,
        loadingMessage: undefined,
      });
    } finally {
      await clearRefreshLock();
    }
  };

  const handleProceedWithWarning = (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card) return;

    if (card.refreshWarningType) {
      // Refresh warning — proceed with refresh, skip checks
      handleRefresh(cardId, true);
    } else {
      // Import warning — proceed with import
      handleImport(cardId);
    }
  };

  const handleCancelWarning = (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card) return;

    if (card.refreshWarningType) {
      // Return to imported state (not pending)
      updateCard(cardId, {
        status: "imported",
        warningMessage: undefined,
        refreshWarningType: undefined,
      });
    } else {
      // Return to pending state
      updateCard(cardId, {
        status: "pending",
        warningMessage: undefined,
        rowCount: undefined,
      });
    }
  };

  const handleDateFilterChangeAndRefresh = async (
    cardId: string,
    dateColumn: string | undefined,
    dateColumnDisplayName: string | undefined,
    datePreset: string | undefined,
    afterMonth: string | undefined,
  ) => {
    // Update card state with new filter first
    updateCard(cardId, {
      dateColumn,
      dateColumnDisplayName,
      datePreset,
      afterMonth,
    });

    // We need to build the refresh manually since state won't have propagated yet
    const card = importCards.find((c) => c.id === cardId);
    if (!card || !selectedOrgId || !card.tableName || !card.sheetId || isOffline) return;

    // Build date filter from the new values (not from card state)
    const dateFilter: DateFilterParam | undefined =
      dateColumn && datePreset
        ? {
            column: dateColumn,
            preset: datePreset,
            ...(datePreset === "after" && afterMonth ? { after_month: afterMonth } : {}),
          }
        : undefined;

    updateCard(cardId, {
      status: "loading",
      loadingMessage: `Refreshing ${card.dataSource.name}…`,
      warningMessage: undefined,
      refreshWarningType: undefined,
    });

    await setRefreshLock();
    try {
      const stream = await executeSourceQueryStream(
        selectedOrgId,
        card.dataSource.id,
        card.dataSource.name,
        dateFilter,
      );

      const writeResult = await refreshTableFromCsvStream(
        card.tableName,
        card.sheetId,
        stream,
        (progressMsg: string) => {
          updateCard(cardId, { loadingMessage: progressMsg });
        },
      );

      let contentHash: string | undefined;
      let columnSchema: string[] | undefined;
      try {
        contentHash = await computeTableContentHash(card.tableName);
        columnSchema = await getTableColumnNames(card.tableName);
      } catch {
        // Non-critical
      }

      const now = new Date().toISOString();
      const updatedCard: ImportCardData = {
        ...card,
        status: "imported",
        dateColumn,
        dateColumnDisplayName,
        datePreset,
        afterMonth,
        lastRefreshed: now,
        rowCount: writeResult.rowCount,
        truncated: writeResult.truncated || false,
        contentHash,
        columnSchema,
        loadingMessage: undefined,
        errorMessage: undefined,
        warningMessage: undefined,
        refreshWarningType: undefined,
      };

      const newXmlPartId = await updateImportMetadata({
        ...updatedCard,
        lastRefreshed: now,
      });

      updateCard(cardId, { ...updatedCard, xmlPartId: newXmlPartId });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        markExpired();
        updateCard(cardId, { status: "imported", loadingMessage: undefined });
        return;
      }
      let message =
        error instanceof Error ? error.message : "Refresh failed";
      if (error instanceof ApiRequestError && error.status === 429) {
        message = "Too many requests \u2014 please wait a moment before refreshing.";
      }
      updateCard(cardId, {
        status: "error",
        errorMessage: message,
        loadingMessage: undefined,
      });
    } finally {
      await clearRefreshLock();
    }
  };

  const handleReimport = async (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card) return;

    // Delete old XML Part
    if (card.xmlPartId) {
      try {
        await deleteImportMetadata(card.xmlPartId);
      } catch {
        // Best-effort
      }
    }

    // Clear old sheet/table references so handleImport creates a new sheet
    updateCard(cardId, {
      status: "pending",
      sheetId: undefined,
      sheetName: undefined,
      tableName: undefined,
      xmlPartId: undefined,
      contentHash: undefined,
      columnSchema: undefined,
      lastRefreshed: undefined,
      errorMessage: undefined,
      sheetDeleted: undefined,
      rowCount: undefined,
    });

    // Trigger a fresh import
    handleImport(cardId);
  };

  const handleRemove = async (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (!card) return;

    if (card.xmlPartId) {
      try {
        await deleteImportMetadata(card.xmlPartId);
      } catch {
        // Best-effort deletion
      }
    }

    setImportCards((prev) => prev.filter((c) => c.id !== cardId));
  };

  const handleRetry = (cardId: string) => {
    const card = importCards.find((c) => c.id === cardId);
    if (card?.sheetId && card?.tableName) {
      handleRefresh(cardId, true); // Already imported — refresh in-place
    } else {
      handleImport(cardId); // Never imported — fresh import
    }
  };

  const handleSheetClick = async (sheetId: string) => {
    try {
      await activateSheetById(sheetId);
    } catch {
      // Sheet may have been deleted
    }
  };

  const handleRefreshAll = async () => {
    const importedCards = importCards.filter((c) => c.status === "imported");
    if (importedCards.length === 0 || isOffline) return;

    setIsRefreshingAll(true);
    await clearRefreshLock(); // Clear any stale locks before starting

    // Set all cards to a queued loading state up front
    const total = importedCards.length;
    for (let i = 0; i < total; i++) {
      updateCard(importedCards[i].id, {
        status: "loading",
        loadingMessage: `Up next (${i + 1} of ${total})…`,
      });
    }

    for (const card of importedCards) {
      try {
        await handleRefresh(card.id, false, true);
      } catch {
        // Individual card shows its own error
      }
    }

    setIsRefreshingAll(false);
  };

  const hasImportedCards = importCards.some((c) => c.sheetId);

  return (
    <div className="relative flex flex-col h-full bg-bg font-display-body">
      <TaskPaneHeader
        onLogout={logout}
        onAddData={() => setPaletteOpen(true)}
        onRefreshAll={handleRefreshAll}
        isRefreshingAll={isRefreshingAll}
        hasImportedCards={hasImportedCards}
      />

      {authState === "expired" && (
        <SessionExpiredBanner onReAuthenticate={login} />
      )}

      {isOffline && <OfflineNotice />}

      {xmlRestoreWarning && (
        <div className="px-4 pt-4">
          <Alert variant="info" size="small" title={xmlRestoreWarning} />
        </div>
      )}

      <div ref={scrollContainerRef} className="relative flex-1 min-h-0 overflow-y-auto px-4 pt-0 pb-4">


        <div className="space-y-3 mt-3">
          {importCards.length === 0 ? (
            <EmptyState onAddData={() => setPaletteOpen(true)} />
          ) : (
            importCards.map((card) => (
              <ImportCard
                key={card.id}
                card={card}
                orgId={selectedOrgId}
                onImport={handleImport}
                onRefresh={handleRefresh}
                onRemove={handleRemove}
                onRetry={handleRetry}
                onSheetClick={handleSheetClick}
                onProceedWithWarning={handleProceedWithWarning}
                onCancelWarning={handleCancelWarning}
                onDateFilterChange={handleDateFilterChange}
                onReimport={handleReimport}
                onDateFilterChangeAndRefresh={handleDateFilterChangeAndRefresh}
              />
            ))
          )}
        </div>
      </div>

      {/* Floating scroll-to-bottom button */}
      {showScrollDown && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <button
            onClick={handleScrollToBottom}
            className="w-8 h-8 rounded-full bg-bg-elevated border border-bg3 shadow-md flex items-center justify-center text-fg2 hover:bg-bg2 transition-colors cursor-pointer"
            aria-label="Scroll to bottom"
          >
            <ChevronDown size={16} />
          </button>
        </div>
      )}

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        orgId={selectedOrgId}
        onSelect={handleSelectSource}
        onAuthExpired={() => login()}
        recentSourceIds={importCards
          .filter((c) => c.status === "imported")
          .map((c) => c.dataSource.id)}
      />
    </div>
  );
}

export function TaskPane() {
  return (
    <AuthProvider>
      <TaskPaneContent />
    </AuthProvider>
  );
}
